import { graphql, rest, DefaultBodyType } from 'msw';

type ContextOptions = {
  delay?: number | 'infinite' | 'real';
  data?: any;
  status?: number;
};

export enum RequestType {
  graphql = 'graphql',
  rest = 'rest',
}

export type MockRequest = {
  type: RequestType;
  pathOrOperationName: string;
  ctxOptions: ContextOptions;
};

const ctxOptionsDefault: ContextOptions = {
  delay: 0,
  data: {},
  status: 200,
};

function getGraphQLHandler(queryOperationName: string, ctxOptions: ContextOptions) {
  return graphql.query(queryOperationName, (_req, res, ctx) => {
    return res(ctx.delay(ctxOptions.delay), ctx.data(ctxOptions.data), ctx.status(ctxOptions.status ?? 200));
  });
}

function getRestHandler(url: string, ctxOptions: ContextOptions) {
  return rest.post(url, (_req, res, ctx) => {
    return res(ctx.delay(ctxOptions.delay), ctx.json(ctxOptions.data), ctx.status(ctxOptions.status ?? 200));
  });
}

/*
 * @param queryOperationName - operation name of the query
 * @param ctxOptions - options for the context
 *   @param ctxOptions.delay - delay in ms, default is 0
 *   @param ctxOptions.data - data to be returned, default is {}
 *   @param ctxOptions.status - status code to be returned, default is 200
 * @returns msw response
 * @example
 * const querySuccessResponse = getQueryResponse('fetchInvoicesForAccountBalance', {data: {}, delay: 1000});
 */
export const getQueryResponse = (queryOperationName: string, ctxOptions = ctxOptionsDefault) => {
  return { msw: [getGraphQLHandler(queryOperationName, ctxOptions)] };
};

/*
 * @param url - url to be mocked
 * @param ctxOptions - options for the context
 *   @param ctxOptions.delay - delay in ms, default is 0
 *   @param ctxOptions.data - json to be returned, default is {}
 *   @param ctxOptions.status - status code to be returned, default is 200
 * @returns msw response
 */
export const getRestResponse = (url: string, ctxOptions = ctxOptionsDefault) => {
  return { msw: [getRestHandler(url, ctxOptions)] };
};

/* This function is used to mock multiple requests in a single story
 * @param type -  query or rest
 * @param ctxOptions - options for the context
 *   @param ctxOptions.delay - delay in ms, default is 0
 *   @param ctxOptions.data - json to be returned, default is {}
 *   @param ctxOptions.status - status code to be returned, default is 200
 * @returns msw[] response
 */
export const getMultipleResponse = (requests: MockRequest[]) => {
  const bodyTypes: DefaultBodyType[] = [];
  // Create an array of type DefaultBodyType
  const responses = { msw: bodyTypes };
  requests.forEach((request) => {
    if (request.type === RequestType.graphql) {
      responses.msw.push(getGraphQLHandler(request.pathOrOperationName, request.ctxOptions));
    } else if (request.type === RequestType.rest) {
      responses.msw.push(getRestHandler(request.pathOrOperationName, request.ctxOptions));
    }
  });
  return responses;
};
